import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { ChevronLeft, BookOpen, Trash2, PenLine } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import T from "../components/T";

export default function SavedReflections() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/reflections");
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { refresh(); }, []);

  const del = async (id) => {
    if (!window.confirm(t("reflections.confirm_remove"))) return;
    try {
      await api.delete(`/reflections/${id}`);
      toast(t("reflections.removed"));
      refresh();
    } catch { toast.error(t("reflections.remove_error")); }
  };

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-saved-reflections">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <p className="font-script text-2xl text-brand">{t("reflections.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight">
          <T i18nKey="reflections.h" />
        </h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">
          <T i18nKey="reflections.intro" />
        </p>

        <div className="mt-4">
          <Button
            data-testid="saved-write-new-btn"
            onClick={() => navigate("/before-you-go-back")}
            className="rounded-full bg-brand hover:bg-brand/90 text-white h-11"
          >
            <PenLine className="w-4 h-4 mr-2" /> {t("reflections.write_new")}
          </Button>
        </div>
      </div>

      <div className="px-8 pb-10 space-y-3">
        {items === null && (
          <div className="rounded-3xl bg-card border border-border p-6 h-24 animate-soft-pulse" />
        )}
        {items && items.length === 0 && (
          <div className="rounded-3xl bg-card border border-dashed border-border p-6 text-center text-foreground/65" data-testid="saved-empty">
            <BookOpen className="w-5 h-5 text-brand mx-auto mb-2" />
            {t("reflections.empty")}
          </div>
        )}
        {items && items.map((r) => (
          <article key={r.id} className="rounded-3xl bg-card border border-border p-5" data-testid={`saved-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                {r.prompt && (
                  <p className="text-xs uppercase tracking-widest text-foreground/55">{r.prompt}</p>
                )}
                <div className="text-[11px] text-foreground/45 mt-0.5">
                  {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <button
                data-testid={`saved-del-${r.id}`}
                aria-label={t("common.delete")}
                onClick={() => del(r.id)}
                className="reflection-delete-btn text-foreground/40 hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-3 font-serif italic text-foreground/85 leading-relaxed whitespace-pre-wrap">
              {r.body}
            </p>
          </article>
        ))}
      </div>
    </AppShell>
  );
}
